/**
 * Why the flow decided the stored session is no longer usable.
 *
 * Passed to the handler because the three cases do not deserve the same response, and a
 * caller cannot tell them apart afterwards:
 *
 * - `refresh-failed` — we presented the stored refresh token and the server rejected it, and
 *   nobody else had written a newer one. The strongest signal available that the session is
 *   actually dead.
 * - `no-refresh-token` — there is an access token in storage but no refresh token beside it,
 *   and the access token is stale. Nothing can be done with it.
 * - `unauthorized` — a request came back 401 that the refresh path could not rescue. The
 *   weakest signal of the three: a 401 can be a per-endpoint authorization problem rather than
 *   a dead session, which is exactly why a host may want to treat it differently.
 */
export type AuthFailureReason = 'refresh-failed' | 'no-refresh-token' | 'unauthorized';

export type AuthFailureHandler = (reason: AuthFailureReason) => void | Promise<void>;

/**
 * The caller's handler, or nothing.
 *
 * ## Why this is a port and not a rule of ours
 *
 * Destroying a session is not our decision to make, because the storage keys are **shared**:
 * onboarding, `@vidahealth/login` and VidaReactNative all read and write `vida_jwt_token`,
 * `vida_refresh_token` and `vida_token_expires` through the same
 * `Platform.OS === 'web' ? sessionStorage : SecureStore` branch. Clearing them signs the
 * member out of whatever else is in the process.
 *
 * We could have tried to earn the right to clear — track which party minted the session, or
 * infer ownership from whether an `apiClient` was injected. Both were considered and both are
 * inference: the first needs a provenance marker in shared storage that only we maintain, and
 * neither can be right in every arrangement. Handing the decision to whoever owns the session
 * removes the question instead of answering it, which is the same move `analytics` and
 * `apiClient` already make in this package. Ownership is expressed by what you pass.
 *
 * ## The default is to do nothing, deliberately
 *
 * Unset, a failure clears nothing. That direction is the safe one: the cost of not clearing is
 * a stale key in storage that the next successful login overwrites, while the cost of clearing
 * wrongly is a member signed out of the host app mid-session. It also means a host that never
 * thinks about this gets the correct behaviour by default, and the party that has to remember
 * something is us — see `app/_layout.tsx`, which passes `clearAuthTokens`.
 *
 * Note the flow still recovers without a handler: the failing paths return `null` rather than a
 * token, so requests go out unauthenticated, and `emitUnauthorized` still fires so
 * `RulesEngineProvider` resets and routes back to the entry screen. What does NOT happen is the
 * storage write.
 *
 * ## Distinct from `subscribeToUnauthorized`
 *
 * That is a broadcast — "our own view of the session should reset" — with any number of
 * listeners, and `RulesEngineProvider` is one. This is a single-owner decision: "the stored
 * session should be destroyed, or not". `rejectUnauthorized` used to fuse the two, which is
 * why clearing a host's keys looked like a detail of resetting our own state.
 */
let handler: AuthFailureHandler | null = null;

/**
 * Publish the caller's handler. Called by `OnboardingRoot` during render, before any child can
 * fire a request; `undefined` clears it, so a later mount that passes nothing does not inherit
 * the previous one.
 */
export const setAuthFailureHandler = (next: AuthFailureHandler | undefined): void => {
  handler = next ?? null;
};

/** Test-only reset — the slot is process-wide and would otherwise leak between tests. */
export const resetAuthFailureHandler = (): void => {
  handler = null;
};

/**
 * Report a failure to whoever owns the session, and wait for them to finish.
 *
 * **Awaited**, unlike the analytics facade's fire-and-forget guard. A handler that clears
 * storage has to have finished before the next request reads through, or the request that
 * triggered the failure would race the cleanup and re-read the tokens we just reported as
 * dead.
 *
 * **Guarded**, for the same reason telemetry is: every caller sits in an axios interceptor or
 * a token accessor, so a handler that throws or rejects would surface as an unrelated failure
 * on whatever request happened to be in flight. A caller's bad handler is their bug to fix,
 * not ours to propagate.
 */
export const notifyAuthFailure = async (reason: AuthFailureReason): Promise<void> => {
  if (!handler) return;
  try {
    await handler(reason);
  } catch (error) {
    console.error(`Auth: onAuthFailure('${reason}') handler failed`, error);
  }
};
